import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import money from "../images/money.png"
import user from "../images/user.png";
import diversification from "../images/diversification.png";
import equity from "../images/equity.png";
import fixedIncomes from "../images/fixed-income.png";
import bonds from "../images/bonds.png";
import bonds2 from "../images/bonds-2.png";
import promotion from "../images/promotion.png";
import buildings from "../images/buildings.png";


/* ================= ANIMATION ================= */

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.18,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const cardAnimation = {
  hidden: { opacity: 0, y: 40, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.55, ease: "easeOut" },
  },
};

/* ================= MAIN SERVICES ================= */

const services = [
  {
    image: money,
    title: "Mutual Funds",
    description:
      "Mutual funds can be a powerful tool for long-term wealth creation when guided by expertise and discipline. Our investment approach cuts through market noise, focusing on a structured and time-tested philosophy that prioritizes your goals, risk profile and aspirations. We combine in-depth analysis with careful fund selection, seeking out managers with a proven track record of consistency and conviction. The result is a curated portfolio designed for stability, resilience, and sustainable growth – helping you build wealth with confidence.",
  },
  {
    image: user,
    title: "Portfolio Management Services",
    description:
      "We offer exclusive access to top-tier portfolio management services (PMS) through partnerships with seasoned investment managers. These experts employ disciplined and research-driven strategies that have consistently delivered results across market cycles. The PMS approach focuses on bottom-up stock selection, driven by in-depth analysis and prudent diversification. For ambitious investors, we cherry-pick select PMS strategies, which target emerging opportunities with strong fundamentals and governance. PMS is typically designed for discerning investors, seeking concentration in stocks for long term wealth creation. ",
  },
  {
    image: diversification,
    title: "Alternative Investment Funds",
    description:
      "We provide access to select alternative investment funds (AIF) through exclusive partnerships with reputed fund managers. These strategies seek to enhance portfolio diversification and risk-adjusted returns by capitalizing on opportunities across infrastructure, real assets, commodities and market inefficiencies. Select offerings also follow structured and lower-volatility approaches aimed at delivering returns superior to traditional fixed-income solutions. Our chosen AIF strategies are curated for discerning investors, seeking differentiated strategies, stability and long-term value creation.",
  },
  {
    image: equity,
    title: "Private Equity Funds",
    description:
      "We offer access to select private equity funds through exclusive partnerships with seasoned investment managers. These funds invest in early-stage technology ventures with high growth potential, as well as in mature businesses raising growth capital to scale operations and expand market presence. Select investments may also benefit from planned value-unlocking events, including potential public market listings. With active involvement, strategic guidance and institutional expertise, these private equity strategies are curated for investors seeking enhanced long-term returns through ownership in rapidly expanding businesses beyond listed markets.",
  },
];

/* ================= FIXED INCOME ================= */

const fixedIncome = [
  {
    image: bonds,
    title: "Corporate Bonds",
    description:
      "High-quality debt instruments issued by corporates offering predictable income with defined maturities. Carefully selected to balance credit quality, yield and stability within a diversified portfolio.",
  },
  {
    image: bonds2,
    title: "Non-Convertible Debentures",
    description:
      "Structured debt securities providing regular income with fixed or floating returns. Suitable for investors seeking higher yields than traditional deposits while maintaining a defined risk framework.",
  },
  {
    image: promotion,
    title: "Market Linked Debentures",
    description:
      "Innovative instruments where returns are linked to underlying market indices or strategies. Designed for sophisticated investors aiming to enhance returns through structured exposure with controlled risk.",
  },
  {
    image: buildings,
    title: "Corporate Fixed Deposits (FD)",
    description:
      "Term deposits offered by reputed corporates providing stable returns higher than bank FDs. Selected with a focus on issuer strength, tenure alignment and capital preservation.",
  },
];




/* ================= COMPONENT ================= */

const ServicesSection = () => {

  const [highlight, setHighlight] = useState(null);

  useEffect(() => {
    const handleHighlight = (event) => {
      const index = event.detail;

      setHighlight(index);

      setTimeout(() => {
        setHighlight(null);
      }, 2500);
    };

    window.addEventListener("highlightService", handleHighlight);

    return () => {
      window.removeEventListener("highlightService", handleHighlight);
    };
  }, []);



  return (
    <section className="py-20 px-5 md:px-12 lg:px-10 lg:mx-20 my-16 font-satoshi">

      {/* Heading */}
      <motion.div
        className="max-w-full mx-auto mb-16"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <h2 className="text-4xl md:text-6xl font-bold text-deepblue">
          Our Services
        </h2>
      </motion.div>

      {/* ===== Main Services ===== */}

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-full lg:mx-auto"
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {services.map((service, index) => (
          <motion.div
            id={`service-${index}`}
            key={index}
            variants={cardAnimation}
            className={`border border-deepblue/10 rounded-3xl px-6 py-8 lg:p-10 bg-white transition-all duration-300
    ${highlight === index
                ? "bg-lightbeige -translate-y-1 shadow-lg"
                : "hover:bg-lightbeige"
              }`}
          >
            <div className="mb-6 inline-flex items-center justify-center 
                  p-3 sm:p-4 md:p-5 
                  bg-offwhite 
                  rounded-xl md:rounded-2xl 
                  shadow-md md:shadow-lg 
                  ">

              <img
                src={service.image}
                alt={service.title}
                className="
        w-10 h-10
        sm:w-12 sm:h-12
        md:w-14 md:h-14
        object-contain
      "
                loading="lazy"
              />
            </div>

            <h3 className="text-2xl lg:text-3xl font-semibold mb-3">
              {service.title}
            </h3>

            <p className="text-gray-600 text-md lg:text-xl leading-relaxed text-justify">
              {service.description}
            </p>
          </motion.div>
        ))}
      </motion.div>

      {/* ===== Fixed Income ===== */}

      <motion.div
        id="service-4"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className={`bg-white max-w-full mx-auto mt-8 border border-deepblue/10 rounded-2xl px-5 py-8 lg:p-12 transition-all duration-300
  ${highlight === 4
            ? "bg-lightbeige -translate-y-1 shadow-lg"
            : "hover:bg-lightbeige hover:-translate-y-1 hover:shadow-lg"
          }`}
      >

        {/* Header */}

        <div className="mb-6 inline-flex items-center justify-center 
                  p-3 sm:p-4 md:p-5 
                  bg-offwhite
                  rounded-xl md:rounded-2xl 
                  shadow-md md:shadow-lg 
                  ">

          <img
            src={fixedIncomes}
            alt="Fixed Income Instruments"
            className="
        w-10 h-10
        sm:w-12 sm:h-12
        md:w-14 md:h-14
        object-contain
      "
            loading="lazy"
          />
        </div>

        <h3 className="text-2xl lg:text-3xl md:text-3xl font-semibold">
          Fixed Income Instruments
        </h3>


        {/* Sub Services */}

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mt-14"
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {fixedIncome.map((item, index) => (
            <motion.div key={index} variants={fadeUp} className="flex flex-col gap-4">

              <div className="flex flex-col items-start">
                <div className="mb-6 justify-start 
  p-3 sm:p-4 md:p-4
  bg-offwhite 
  rounded-xl md:rounded-2xl 
  shadow-md md:shadow-lg">

                  <img
                    src={item.image}
                    alt={item.title}
                    className="
      w-8 h-8
      sm:w-10 sm:h-10
      md:w-10 md:h-10-
      object-contain
    "
                    loading="lazy"
                  />
                </div>
                <div className="lg:min-h-[70px]">
                  <h4 className="font-semibold lg:text-2xl text-xl leading-snug">
                    {item.title}
                  </h4>
                </div>

                <div className="mt-2 h-[2px] lg:h-[3px] lg:w-72 w-64 bg-deepblue" />

                <p className="mt-6 text-gray-600 text-md lg:text-xl leading-relaxed text-justify">
                  {item.description}
                </p>

              </div>

            </motion.div>
          ))}
        </motion.div>

      </motion.div>

    </section >
  );
};

export default ServicesSection;
